export interface DocumentationContent {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: string;
  lastUpdated: string;
  readTime: string;
  tags: string[];
  content: string; // Conteúdo em formato de texto/markdown
}

export const documentationContent: DocumentationContent[] = [
  {
    id: 'introduction',
    title: 'Introdução ao Sistema',
    description: 'Conheça o GettelWeb, o sistema inteligente de gestão e criação de contratos para venda e renegociação de linhas corporativas TIM Black Empresas.',
    category: 'Introdução',
    icon: 'rocket',
    lastUpdated: '2025-01-20',
    readTime: '5 min',
    tags: ['introdução', 'sistema', 'visão geral'],
    content: `# Sistema Inteligente de Gestão de Contratos da Gettel

## 🏢 Introdução

O **Gettelweb** é o sistema inteligente de **gestão e criação de contratos** desenvolvido pela **Gettel**, voltado para a **venda e renegociação de linhas corporativas TIM Black Empresas**.

Seu objetivo é **agilizar e padronizar o processo comercial**, desde a consulta de clientes e geração de contratos até o envio para assinatura eletrônica via DocuSign.

O sistema foi criado para uso interno da equipe Gettel e parceiros autorizados, focando em **pessoas jurídicas (CNPJs)** — tanto **clientes ativos da TIM** quanto **novos clientes empresariais**.

## 🧭 Acesso ao Sistema

- **Endereço:** https://gettelweb.com.br

![Tela de Login do Sistema](login.png)

- **Perfis de acesso:**
  - **Administrador:** visualiza e gerencia todas as funcionalidades e usuários.
  - **Líder de Equipe:** gerencia apenas os contratos de sua equipe.
  - **Operador:** cria e gerencia seus próprios contratos.`
  },
  {
    id: 'dashboard',
    title: 'Dashboard - Visão Geral',
    description: 'Entenda como visualizar métricas operacionais e financeiras do sistema através do Dashboard.',
    category: 'Introdução',
    icon: 'chart bar',
    lastUpdated: '2025-01-20',
    readTime: '3 min',
    tags: ['dashboard', 'métricas', 'indicadores'],
    content: `# 📊 Página — Dashboard

**Objetivo:** Exibir métricas operacionais e financeiras do sistema.

**Principais informações:**

- Total de contratos **Criados**, **Em análise**, **Assinados** e **Aprovados**.
- Receita obtida a partir dos contratos finalizados.

![Dashboard principal do sistema](dashboard.png)

**Regras:**

- Algumas informações financeiras e indicadores consolidados aparecem **apenas para administradores**.`
  },
  {
    id: 'clients',
    title: 'Gestão de Clientes',
    description: 'Aprenda a consultar e visualizar informações de clientes cadastrados, incluindo consulta de CNPJs na Receita Federal.',
    category: 'Clientes',
    icon: 'building',
    lastUpdated: '2025-01-20',
    readTime: '5 min',
    tags: ['clientes', 'cnpj', 'consulta'],
    content: `# 🧾 Página — Clientes

**Objetivo:** Consultar e visualizar informações de clientes cadastrados.

![Lista de Clientes](clientes.png)

**Funções:**

- Exibe a lista completa de clientes registrados.
- Permite **consultar CNPJs diretamente na Receita Federal**.

> **Observação:** a consulta de CNPJ também está disponível dentro da página de criação de contratos.`
  },
  {
    id: 'contracts-overview',
    title: 'Visão Geral de Contratos',
    description: 'Entenda como visualizar e gerenciar todos os contratos criados no sistema, com diferentes níveis de acesso.',
    category: 'Contratos',
    icon: 'file alternate',
    lastUpdated: '2025-01-20',
    readTime: '4 min',
    tags: ['contratos', 'visualização', 'permissões'],
    content: `# 📃 Página — Contratos

**Objetivo:** Gerenciar todos os contratos criados no sistema.

![Lista de Contratos](contratos.png)

**Regras de visualização:**

- **Operadores:** visualizam apenas seus próprios contratos.
- **Líderes:** visualizam contratos da sua equipe.
- **Administradores:** visualizam contratos de todos os usuários.`
  },
  {
    id: 'contract-creation',
    title: 'Criação de Contrato - Guia Completo',
    description: 'Passo a passo completo para criar um novo contrato no sistema, desde a busca do cliente até a finalização.',
    category: 'Contratos',
    icon: 'file text',
    lastUpdated: '2025-01-20',
    readTime: '15 min',
    tags: ['contratos', 'criação', 'tutorial', 'passo a passo'],
    content: `# 🧾 Criação de Contrato

O fluxo de criação de um novo contrato segue um processo guiado dentro de um **modal dividido em abas (ou etapas)**.

## 🔹 Etapa 1 — Buscar Cliente

1. Clique em **Contratos → Novo Contrato**.

![Etapa 1 - Botão Novo Contrato](contratos-step1.png)

2. No campo **Buscar Cliente**, digite o **CNPJ**.
   - Se o cliente já estiver cadastrado, ele aparecerá em **Clientes Encontrados**.
   - Caso contrário, clique em **Consultar CNPJ**.

![Etapa 1 - Buscar Cliente por CNPJ](contratos-step1-2.png)

3. O sistema buscará os dados na Receita Federal e exibirá a situação cadastral.
   - ⚠️ **Somente é permitido criar contratos para empresas com status "Ativa".**

## 🔹 Etapa 2 — Dados do Contrato

![Etapa 2 - Dados do Contrato](contratos-step2.png)

Campos disponíveis:

- **Tipo de contrato:**
  - *Contrato Normal* → cliente já possui linha com a TIM.
  - *1ª Venda* → novo cliente.
- **Token:** opcional; usado para vincular o contrato ao CRM VTME.
- **Dia de vencimento:** livre escolha do cliente.
- **Número P2B:** opcional.
- **Fastchip:** marcar apenas se o envio for feito pela Gettel.
- Caso não seja identificada a **Inscrição Estadual (IE)** do cliente, aparecerá um **alerta de monitoramento**.

## 🔹 Etapa 3 — Linhas

Nesta aba, são adicionadas as **linhas aditivas e de renegociação**.

![Etapa 3 - Adicionar Linhas](contratos-step3.png)

### Aditivo:
- Inserir o **DDD**.
- Se for *fastchip*, sistema seleciona um **ICCID disponível**.
- Selecionar o **plano** (apenas ofertas compatíveis com aditivo).
- Selecionar **quantidade** caso seja mais de uma linha com o mesmo plano.

![Etapa 3 - Seleção de Plano](contratos-step4.png)

### Renegociação:
- Inserir o **número da linha atual do cliente** (não ICCID).
- Selecionar o plano de renegociação.

![Etapa 3 - Renegociação](contratos-step6.png)

Após preencher, clique em **Adicionar Linha**.

Repita o processo para todas as linhas envolvidas no contrato.

## 🔹 Etapa 4 — Endereço de Entrega

![Etapa 4 - Endereço de Entrega](contratos-step7.png)

- Botões disponíveis:
  - **Usar endereço do cliente** (usa o endereço cadastrado no CNPJ).
  - **Informar manualmente** (caso o cliente queira entrega em outro local).

## 🔹 Etapa 5 — Administrador do Contrato

![Etapa 5 - Administrador do Contrato](contratos-step5.png)

Defina quem será o **responsável pelo contrato dentro da empresa do cliente**.

- Clique em **Selecionar QSA/Partner** para escolher entre os representantes oficiais da empresa.
- Caso não exista, insira os dados manualmente.
- As abas de **Representantes** e **Recebedores** puxam, por padrão, o mesmo nome e CPF do administrador (podem ser editadas).
- A aba de **Testemunha** geralmente não é utilizada.

## 🔹 Conclusão

Após finalizar, o sistema exibirá:

> "Contrato criado com sucesso."

![Contrato Criado com Sucesso](contrato-sucesso.png)

- Clique em **Baixar Contratos** → o sistema fará o download em \`.zip\`.

![Baixar Contratos](contratos-step8.png)

- Se houver aditivos e renegociações, **escolha sempre separar**.

![Separar Contratos](contrato-separacao.png)

- Em seguida, prossiga para o envio via **DocuSign**.

Se o status *assinado* não for marcado imediatamente, o contrato aparecerá na **barra superior de notificações**, aguardando confirmação posterior.`
  },
  {
    id: 'docusign',
    title: 'Envio e Assinatura via DocuSign',
    description: 'Guia completo para enviar contratos para assinatura eletrônica via DocuSign, incluindo preparação de envelopes e campos de assinatura.',
    category: 'Contratos',
    icon: 'signature',
    lastUpdated: '2025-01-20',
    readTime: '12 min',
    tags: ['docusign', 'assinatura', 'envio', 'tutorial'],
    content: `# 📩 Envio e Assinatura via DocuSign

## 🧾 Etapa 1 — Baixar os arquivos do contrato

1. Clique em **Baixar Contratos** após a criação.
2. Descompacte o arquivo \`.zip\`.

![Arquivo ZIP dos Contratos](arquivo-rar-contratos.png)

3. Certifique-se de separar os contratos de aditivo e renegociação.

## ✉️ Etapa 2 — Criar e preparar o envelope no DocuSign

1. Na aba de Início, vá em **Iniciar → Envelopes → Enviar um Envelope**.

![Tela Inicial do DocuSign](home-docusign.png)

2. Clique em **Fazer Upload** e adicione os PDFs do contrato.
   - 💡 **Dica:** arraste diretamente os arquivos descompactados do \`.zip\` para dentro da página do DocuSign — o sistema reconhecerá automaticamente.

![Upload de PDFs no DocuSign](upload-docusign.png)

**Preenchendo os dados do destinatário:**

![Dados do Destinatário](destinatario-docusign.png)

- Informe o **Nome** do cliente.
- Em **Opções de entrega**, **selecione apenas "WhatsApp"**.
- Adicione o **número de WhatsApp** do responsável pela assinatura.
- Adicione um Assunto
- Clique em **Próximo**.

## 🧱 Etapa 3 — Inserindo os campos de assinatura

1. O PDF será aberto com duas colunas laterais:
   - Esquerda → campos disponíveis.
   - Direita → visualização do PDF.

2. Na coluna esquerda:
   - Clique na **chave inglesa (campos personalizados)**.

![Campos Personalizados](campos-personalizados-pdf.png)

   - Escolha o campo **\`SIGN_REPRESENTANTE\`**.
   - Clique sobre o PDF para aplicar os campos automaticamente.
   - Confirme os modais que aparecerem.

![Confirmar Campo 1](confirmar-campo-docusign1.png)

![Confirmar Campo 2](confirmar-campo-docusign2.png)

## 📤 Etapa 4 — Enviar para assinatura

- Revise os campos aplicados.
- Clique em **Enviar**.

![Confirmar Assinatura](confirmar-assinatura-pdf.png)

- O cliente receberá o contrato diretamente no **WhatsApp** com o link de assinatura.

## 📬 Etapa 5 — Confirmar no Gettelweb

1. Após assinatura, o DocuSign marcará o envelope como **Concluído**.
2. Retorne ao Gettelweb → **Notificações**.

![Notificações no GettelWeb](notificacao-gettelweb.png)

3. Clique em **Confirmar como Assinado**.

![Marcar Contrato como Assinado](contrat-marcar-assinado.png)

## ⚠️ Dicas e Boas Práticas

- Sempre verifique se o **CNPJ está ativo** antes de enviar.
- Prefira **envelopes separados** para cada tipo de contrato.
- Confira se todos os PDFs estão anexados corretamente.
- Use sempre o campo **\`SIGN_REPRESENTANTE\`**.

## 🛠️ Problemas Comuns e Soluções Rápidas no DocuSign

| Problema | Causa provável | Solução |
| --- | --- | --- |
| ❌ Arquivo não sobe no DocuSign | ZIP não descompactado ou formato incorreto | Descompacte o arquivo e envie apenas os PDFs |
| ⚠️ Campo de assinatura não aparece | Campo \`SIGN_REPRESENTANTE\` não aplicado | Reabra o PDF e aplique novamente pelo ícone de chave inglesa |
| 📱 Cliente não recebeu o link | Número de WhatsApp incorreto ou com formato errado | Revise o número e garanta que o "+55" foi adicionado |
| 🕓 Assinatura pendente há muito tempo | Cliente não abriu o link | Reenvie o envelope ou entre em contato com o cliente |
| 🧩 Campos fora de posição | Documento foi alterado antes do envio | Gere novamente o contrato no Gettelweb e reenvie |
| 🔒 Envelope travado | Sessão do DocuSign expirou | Faça logout e login novamente, depois reabra o envelope`
  },
  {
    id: 'iccid',
    title: 'Gestão de ICCIDs',
    description: 'Aprenda a gerenciar ICCIDs disponíveis, incluindo criação e importação via planilha Excel.',
    category: 'Administração',
    icon: 'microchip',
    lastUpdated: '2025-01-20',
    readTime: '5 min',
    tags: ['iccid', 'administração', 'importação'],
    content: `# 🧱 Página — ICCID

**Acesso restrito a administradores.**

![Página de ICCIDs](iccids.png)

**Funções:**

- Listagem de todos os ICCIDs disponíveis.
- Criação e importação de ICCIDs via planilha Excel.`
  },
  {
    id: 'users',
    title: 'Gestão de Usuários',
    description: 'Como gerenciar usuários do sistema, incluindo criação, edição e controle de permissões.',
    category: 'Administração',
    icon: 'users',
    lastUpdated: '2025-01-20',
    readTime: '6 min',
    tags: ['usuários', 'permissões', 'administração'],
    content: `# 👥 Página — Usuários

**Acesso restrito a administradores.**

![Página de Usuários](usuarios.png)

**Funções:**

- Listagem de usuários.
- Criação, edição e controle de permissões.`
  },
  {
    id: 'simulation',
    title: 'Simulação de Condições Comerciais',
    description: 'Entenda como utilizar o simulador de condições comerciais baseado em relatórios de fidelidade.',
    category: 'Contratos',
    icon: 'calculator',
    lastUpdated: '2025-01-20',
    readTime: '8 min',
    tags: ['simulação', 'fidelidade', 'condições comerciais'],
    content: `# 💡 Página — Simulação (Em implementação)

**Objetivo:** Simular condições comerciais com base em relatórios de fidelidade.

![Página de Simulação](simulacao.png)

**Detalhes:**

- O simulador utiliza dados importados de um **arquivo Excel de Relatório de Fidelidade**.
- Atualmente, **não está disponível para todos os usuários**.`
  },
  {
    id: 'teams',
    title: 'Gestão de Times',
    description: 'Aprenda a criar e gerenciar times, incluindo estrutura hierárquica de operadores e líderes.',
    category: 'Administração',
    icon: 'users',
    lastUpdated: '2025-01-20',
    readTime: '7 min',
    tags: ['times', 'equipes', 'hierarquia', 'administração'],
    content: `# 🧩 Página — Times

**Acesso restrito a administradores.**

![Página de Times](times.png)

**Funções:**

- Criar times e adicionar membros.
- Gerenciar a estrutura hierárquica de operadores e líderes.`
  }
];

